import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';

import { Header } from './Header/Header';
import { SideBar } from './SideBar/SideBar';
import { SideBarResponsive } from './SideBar/SideBarResponsive';
import { UserManuals } from './UserManuals/UserManuals';

import {
  BellIcon,
  EmailIcon,
  SidebarHamburgerIcon,
  TuosLogoFull,
} from '@components/Icons';
import { UserMenus } from '@components/UserMenus';
import { useAuthSession } from 'src/auth/hooks/useAuthSession';

interface IProps {
  children: React.ReactNode;
}

export const Layout: React.FC<IProps> = ({ children }) => {
  const [showSideBar, setShowSideBar] = useState(true);
  const [showSideBarResponsive, setShowSideBarResponsive] = useState(false);
  const [sideBarBot, setSideBarBot] = useState(false);
  const { workspaceContext } = useAuthSession();
  const [manualList, setManualLists] = useState(false);

  const handleSideBarButton = () => {
    setShowSideBar(!showSideBar);
    setSideBarBot(true);
  };

  const setSideBarClass = sideBarBot ? '' : 'rotate-0';

  return (
    <>
      {/* Desktop */}
      <div className="lg:block md:hidden hidden lg:h-full lg:w-full">
        <div className="grid h-full gap-0 grid-cols-6 ">
          {showSideBar && <SideBar />}

          <div
            className={`h-full ${
              showSideBar || manualList ? 'col-span-5' : 'col-span-6'
            }`}
          >
            <div>
              <Header>
                <div className="w-full items-center justify-between flex h-full">
                  {/* Left: hamburger + workspace label */}
                  <div className="flex items-center gap-3">
                    <div className="bg-[#53CC96] pl-6">
                      <button
                        className={`${setSideBarClass}`}
                        onClick={handleSideBarButton}
                      >
                        <SidebarHamburgerIcon height="24" width="24" />
                      </button>
                    </div>

                    {workspaceContext && (
                      <Link
                        to="/auth/workspace-select"
                        state={{ userInitiated: true }}
                        className="flex flex-col leading-tight hover:opacity-75 transition-opacity"
                      >
                        <span className="text-[10px] text-white/70  uppercase tracking-wide">
                          {workspaceContext.tenantName}
                        </span>
                        <span className="text-sm font-semibold text-white">
                          {workspaceContext.companyName ??
                            workspaceContext.tenantName}
                        </span>
                      </Link>
                    )}
                  </div>

                  <div className="flex items-center h-full pr-[2.5rem]">
                    <div className=" flex text-primary-700">
                      <button className="text-md items-center rounded-full mr-4">
                        <BellIcon height="20" width="20" />
                      </button>
                      <button className="text-md items-center rounded-full mr-4">
                        <EmailIcon height="20" width="20" />
                      </button>
                      <button
                        className="text-md items-center rounded-full mr-4"
                        onClick={() => setManualLists(true)}
                      >
                        <QuestionMarkCircleIcon className="text-gray-500 w-5 h-5" />
                      </button>
                    </div>
                    <UserMenus />
                  </div>
                </div>
              </Header>
            </div>
            <div className="mx-4">{children}</div>
          </div>
        </div>
      </div>

      {/* Mobile & Desktop */}
      <div className="lg:hidden md:block block">
        {/* Header */}
        <div className="flex items-center flex-row justify-between bg-[#53CC96] py-5 px-5">
          {/* Left: hamburger + workspace label */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowSideBarResponsive(!showSideBarResponsive)}
            >
              <SidebarHamburgerIcon height="24" width="24" />
            </button>

            {workspaceContext && (
              <Link
                to="/auth/workspace-select"
                className="flex flex-col leading-tight hover:opacity-75 transition-opacity"
              >
                <span className="text-[10px] text-white/70 uppercase tracking-wide">
                  {workspaceContext.tenantName}
                </span>
                <span className="text-sm font-semibold text-white">
                  {workspaceContext.companyName ?? workspaceContext.tenantName}
                </span>
              </Link>
            )}
          </div>

          {/* Centre: logo always visible */}
          <TuosLogoFull width="104" height="30" />

          <UserMenus />
        </div>
        <div className="flex relative">
          <div className="absolute h-screen bg-[#E7EEEB] z-10">
            {showSideBarResponsive && (
              <>
                <SideBarResponsive
                  onClose={() => setShowSideBarResponsive(false)}
                />
              </>
            )}
          </div>
          <div className="w-full">
            <button onClick={() => setManualLists(true)}>
              <QuestionMarkCircleIcon className="text-gray-500 w-5 h-5 absolute right-4" />
            </button>
            {children}
          </div>
        </div>
      </div>
      {manualList && <UserManuals setUserManual={setManualLists} />}
    </>
  );
};
